import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import SolidCalendarAltIcon from 'LuxKit/Icons/solid/SolidCalendarAltIcon'
import LineRedoIcon from 'LuxKit/Icons/line/LineRedoIcon'
import SolidUnlockAltIcon from 'LuxKit/Icons/solid/SolidUnlockAltIcon'
import useLoyaltyAccount from 'loyalty/hooks/useLoyaltyAccount'
import useCurrentLoyaltyActivities from 'loyalty/hooks/useCurrentLoyaltyActivities'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { getCurrentMembershipYear, getTierChangesInMembershipYear } from 'loyalty/lib/membershipYearUtils'
import { getTierOrder, getTierRank } from 'loyalty/lib/tierUtils'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierDarkBackgroundStyle } from 'styles/utils/tierStyles'

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const TimelineEntry = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${rem(16)};
`

const TimelineIconColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`

const TimelineIconCircle = styled.div`
  width: ${rem(26)};
  height: ${rem(26)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.one};
  color: ${props => props.theme.palette.neutral.default.one};
  flex-shrink: 0;
`

const TimelineLine = styled.div`
  width: ${rem(6)};
  flex: 1;
  min-height: ${rem(16)};
  margin: ${rem(-1)} 0;
  ${tierDarkBackgroundStyle}
`

const TimelineContent = styled.div`
  padding-bottom: ${rem(28)};
  padding-top: ${rem(4)};
`

interface TimelineItemProps {
  icon: React.ReactElement
  date: React.ReactNode
  title: string
  description?: React.ReactNode
  isLast?: boolean
  tier: App.LoyaltyTier
}

function TimelineItem({ icon, date, title, description, isLast, tier }: TimelineItemProps) {
  return <TimelineEntry>
    <TimelineIconColumn>
      <TimelineIconCircle>{icon}</TimelineIconCircle>
      {!isLast && <TimelineLine className={`tier-${tier}`} />}
    </TimelineIconColumn>
    <TimelineContent>
      <BodyText variant="small" colour="neutral-two">{date}</BodyText>
      <BodyText variant="medium" weight="bold">{title}</BodyText>
      {description && <BodyText variant="medium" colour="neutral-three">{description}</BodyText>}
    </TimelineContent>
  </TimelineEntry>
}

function LoyaltyMembershipYearModal() {
  const intl = useIntl()
  const account = useLoyaltyAccount().data
  const [activities] = useCurrentLoyaltyActivities()
  const [tier, tierName] = useCurrentLoyaltyTier()
  const statusReviewDate = account?.statusReviewDate

  const enrolmentDate = useMemo(() => {
    const enrolment = activities.find(a => a.type === 'enrolment')
    return enrolment?.date
  }, [activities])

  const lowerTierName = useMemo(() => {
    const rank = getTierRank(tier)
    if (rank <= 0) return undefined
    const tierOrder = getTierOrder()
    return getLoyaltyTierDisplayName(tierOrder[rank - 1])
  }, [tier])

  const membershipYear = useMemo(() => {
    if (!statusReviewDate) return undefined
    return getCurrentMembershipYear(statusReviewDate, enrolmentDate)
  }, [statusReviewDate, enrolmentDate])

  const tierChanges = useMemo(() => {
    if (!membershipYear) return []
    return getTierChangesInMembershipYear(activities, membershipYear)
  }, [activities, membershipYear])

  const hasTierChanges = tierChanges.length > 0

  // Show "next tier review" when statusReviewDate extends beyond the current membership year end
  // (happens when member has upgraded — their tier is reviewed at end of NEXT year)
  const showNextTierReview = useMemo(() => {
    if (!statusReviewDate || !membershipYear) return false
    const reviewDate = new Date(statusReviewDate)
    return reviewDate.getTime() > membershipYear.end.getTime()
  }, [statusReviewDate, membershipYear])

  const title = intl.formatMessage({
    id: 'L0-PJ-igbwmAmnUNdpTb',
    defaultMessage: 'How your membership year works',
    description: 'Title for membership year explanation modal on the Soci\u00e9t\u00e9 landing page',
  })

  const tierChangeDescription = statusReviewDate
    ? intl.formatMessage({
        id: '2EhwU3wK3peyLA9-Zsqi',
        defaultMessage: 'Your {tierName} benefits are now yours until {reviewDate} or your next tier upgrade.',
        description: 'Description explaining how long upgraded tier benefits last, until review date or next upgrade',
      }, {
        tierName,
        reviewDate: <FormatDateTime value={statusReviewDate} format="day-long-month-long-year" />,
      })
    : intl.formatMessage({
        id: 'Gl2WFkBApeF4y-_uopva',
        defaultMessage: 'Your {tierName} benefits are now yours for the remainder of your membership year.',
        description: 'Description explaining upgraded tier benefits last until end of membership year only',
      }, { tierName })

  const nextTierReviewDescription = lowerTierName
    ? intl.formatMessage({
        id: 'DWqgjPn7Ja_dhOL6_fMI',
        defaultMessage: 'We\u2019ll review the Status Credits and Nights you\u2019ve earned since {reviewDate} to determine whether you retain {tierName} or move to {lowerTierName} for the year ahead.',
        description: 'Description of next tier review date in the membership year modal',
      }, {
        reviewDate: membershipYear ? <FormatDateTime value={membershipYear.end.toISOString()} format="day-long-month-long-year" /> : '',
        tierName,
        lowerTierName,
      })
    : intl.formatMessage({
        id: 'dC9AGman54T_3q7lETgY',
        defaultMessage: 'We\u2019ll review the Status Credits and Nights you\u2019ve earned since {reviewDate} to determine your tier for the year ahead.',
        description: 'Description of next tier review for lowest tier members in the membership year modal',
      }, {
        reviewDate: membershipYear ? <FormatDateTime value={membershipYear.end.toISOString()} format="day-long-month-long-year" /> : '',
      })

  const membershipYearEndsDescription = hasTierChanges
    ? intl.formatMessage({
        id: 'Qt_N3GumMdaIVXoNoguu',
        defaultMessage: 'Your Status Credits and Nights reset to zero. Your {tierName} benefits are completely unaffected and are available to you until your next tier review.',
        description: 'Description of year end when member has upgraded tier, benefits continue until tier review',
      }, { tierName })
    : lowerTierName
      ? intl.formatMessage({
          id: 'vSWWDMJgnhNQXp8jvEOB',
          defaultMessage: 'We\u2019ll review the Status Credits and Nights you\u2019ve earned since {startDate} to determine whether you retain {tierName} or move to {lowerTierName} for the year ahead. Your Status Credits and Nights reset to zero.',
          description: 'Description of year end review when member has not upgraded',
        }, {
          startDate: membershipYear ? <FormatDateTime value={membershipYear.start.toISOString()} format="day-long-month-long-year" /> : '',
          tierName,
          lowerTierName,
        })
      : intl.formatMessage({
          id: '_jIDnMYarTsBEtm4Dbpu',
          defaultMessage: 'We\u2019ll review the Status Credits and Nights you\u2019ve earned since {startDate} to determine your tier for the year ahead. Your Status Credits and Nights reset to zero.',
          description: 'Description of year end review for lowest tier members who cannot be demoted further',
        }, {
          startDate: membershipYear ? <FormatDateTime value={membershipYear.start.toISOString()} format="day-long-month-long-year" /> : '',
        })

  return <Modal title={title}>
    <Group gap={24} direction="vertical">
      <Group gap={8} direction="vertical">
        <BodyText variant="medium" colour="neutral-two">
          <FormattedMessage
            id="bemxP4aulOhFbfYBww4q"
            defaultMessage="Your membership year begins on the date you join Société and runs for 12 months."
            description="First paragraph explaining membership year duration"
          />
        </BodyText>
        <BodyText variant="medium" colour="neutral-two">
          <FormattedMessage
            id="GZPYgYZWa6ZCz1Ro_Eiu"
            defaultMessage="Each year, you earn Status Credits and Nights - these reset when your year ends. But your tier doesn't. Any tier you unlock during the year is yours for the remainder of that membership year, PLUS the full year that follows."
            description="Second paragraph explaining status credits reset and tier retention policy"
          />
        </BodyText>
      </Group>

      {membershipYear && <Group gap={12} direction="vertical">
        <Heading variant="heading5">
          {intl.formatMessage({
            id: 'FJOGG1JInrE2MtZa5Qv9',
            defaultMessage: 'Your Status Journey',
            description: 'Heading for status journey timeline in membership year modal',
          })}
        </Heading>

        <TimelineContainer>
          {/* Membership year start */}
          <TimelineItem
            tier={tier}
            icon={<SolidCalendarAltIcon size={12} />}
            date={<FormatDateTime value={membershipYear.start.toISOString()} format="long-month-long-year" />}
            title={intl.formatMessage({
              id: 'VZ8qz2dkw5az5ZM75yvt',
              defaultMessage: 'Membership year started',
              description: 'Timeline item title for when the membership year began',
            })}
            description={intl.formatMessage(
              {
                id: '_dCU1CdIJ7tqzbj7MLJe',
                defaultMessage: 'Your {yearRange} membership year began.',
                description: 'Description of when the membership year started and at which tier',
              },
              {
                yearRange: `${membershipYear.start.getFullYear()}\u2013${membershipYear.end.getFullYear()}`,
              },
            )}
          />

          {/* All tier changes within the membership year */}
          {tierChanges.map((change) => {
            const changeTierName = change.unlockedTierName ?? tierName
            return <TimelineItem
              key={change.date}
              tier={tier}
              icon={<SolidUnlockAltIcon size={12} />}
              date={<FormatDateTime value={change.date} format="day-long-month-long-year" />}
              title={intl.formatMessage({
                id: '0cdLYQsH4t_M0om_s_BN',
                defaultMessage: 'You unlocked {tierName}',
                description: 'Timeline title for tier upgrade achievement in membership year modal',
              }, { tierName: changeTierName })}
              description={tierChangeDescription}
            />
          })}

          {/* Membership year ends */}
          <TimelineItem
            tier={tier}
            icon={<LineRedoIcon size={12} />}
            date={<FormatDateTime value={membershipYear.end.toISOString()} format="day-long-month-long-year" />}
            title={intl.formatMessage({
              id: 'AbU8uz7nyyWTGS3xJ2Mu',
              defaultMessage: 'Membership year ends',
              description: 'Timeline item title for when the membership year ends and status resets',
            })}
            description={membershipYearEndsDescription}
            isLast={!showNextTierReview}
          />

          {/* Next tier review (shown when statusReviewDate extends beyond current year) */}
          {showNextTierReview && statusReviewDate && <TimelineItem
            tier={tier}
            icon={<SolidCalendarAltIcon size={12} />}
            date={<FormatDateTime value={statusReviewDate} format="day-long-month-long-year" />}
            title={intl.formatMessage({
              id: 'Tnm6jSz9yQ8Kdz8sN9s8',
              defaultMessage: 'Your next tier review',
              description: 'Timeline item title for when the next tier review occurs',
            })}
            description={nextTierReviewDescription}
            isLast
          />}
        </TimelineContainer>
      </Group>}
    </Group>
  </Modal>
}

export default LoyaltyMembershipYearModal
